import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { Church, MyAccount, Plan } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { EmailVerificationNotice } from '../account/EmailVerificationNotice';
import { PlanPicker } from '../billing/PlanPicker';
import { TERMS_VERSION } from '../legal/TermsPage';
import { useMe, useSession } from '../session/SessionProvider';
import { LocationPicker, type LatLng } from '../../shared/ui/LocationPicker';
import { COUNTRIES_FIRST, COUNTRIES_REST } from '../../shared/geo/places';

export function toSlug(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

/**
 * Registrar una Iglesia: requiere correo confirmado. Quien la registra queda como
 * administrador y la Iglesia empieza la prueba gratuita del plan elegido.
 */
export function CreateChurchPage() {
  const me = useMe();
  const data = useAsync(
    (signal) =>
      Promise.all([api.get<MyAccount>('/identity/me', signal), api.get<{ items: Plan[]; newChurchActivation?: 'auto' | 'manual' }>('/saas/plans', signal)]),
    [me.personId],
  );

  return (
    <div className="page page--narrow">
      <header className="page__header">
        <h1>Registrar mi iglesia</h1>
        <p className="muted">Serás el primer administrador. Después podrás invitar a tu equipo y a tus miembros.</p>
      </header>
      {data.status === 'loading' ? <Loading /> : null}
      {data.status === 'error' ? <ErrorState error={data.error} onRetry={data.reload} /> : null}
      {data.status === 'ready' ? (
        !data.data[0].emailVerified ? (
          <Card>
            <EmailVerificationNotice email={data.data[0].email}>Ábrelo para poder registrar tu iglesia.</EmailVerificationNotice>
          </Card>
        ) : data.data[1].items.length === 0 ? (
          <Card>
            <EmptyState title="Todavía no hay planes disponibles.">
              <p className="muted">La administración de KEORA publicará los planes pronto.</p>
            </EmptyState>
          </Card>
        ) : (
          <CreateChurchForm plans={data.data[1].items} manual={data.data[1].newChurchActivation === 'manual'} />
        )
      ) : null}
    </div>
  );
}

function CreateChurchForm({ plans, manual }: { plans: Plan[]; manual: boolean }) {
  const { switchContext } = useSession();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [planCode, setPlanCode] = useState(plans.length === 1 ? plans[0]!.code : '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [spot, setSpot] = useState<LatLng | null>(null);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const f = new FormData(event.currentTarget);
    const t = (k: string) => String(f.get(k) ?? '').trim();
    const missing = [!name.trim() && 'el nombre', !t('address') && 'la dirección', !t('city') && 'la ciudad', !t('state') && 'el estado', !spot && 'el marcador en el mapa', !planCode && 'el plan', !f.get('terms') && 'aceptar los Términos y Condiciones'].filter(Boolean);
    if (missing.length) {
      setError(`Falta ${missing.join(', ')}.`);
      return;
    }
    const location = { address: t('address'), city: t('city'), state: t('state'), country: t('country') || null, latitude: spot!.lat, longitude: spot!.lng };
    setBusy(true);
    setError(null);
    try {
      const church = await api.post<Church>('/multichurch/churches', { name, slug, planCode, timezone: browserTimezone(), location, termsVersion: TERMS_VERSION });
      await switchContext(church.id);
      navigate('/app/facturacion', { replace: true });
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  const trialDays = plans.find((p) => p.code === planCode)?.trialDays ?? plans[0]!.trialDays;
  return (
    <Card>
      <form className="form" onSubmit={onSubmit} noValidate>
        {error ? <Alert>{error}</Alert> : null}
        <Field
          label="Nombre de la iglesia"
          name="name"
          required
          minLength={2}
          maxLength={150}
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (!slugTouched) setSlug(toSlug(e.target.value));
          }}
        />
        <Field
          label="Enlace de tu página"
          name="slug"
          required
          minLength={3}
          maxLength={60}
          hint={`Así quedará: mykeora.com/i/${slug || 'tu-iglesia'} · solo minúsculas, números y guiones.`}
          value={slug}
          onChange={(e) => {
            setSlugTouched(true);
            setSlug(e.target.value);
          }}
        />
        <fieldset className="form__group">
          <legend>¿Dónde está la iglesia?</legend>
          <Field label="Dirección" name="address" required maxLength={300} placeholder="Calle, número y colonia" />
          <div className="form__row form__row--3">
            <Field label="Ciudad" name="city" required maxLength={120} />
            <Field label="Estado" name="state" required maxLength={120} />
            <div className="field">
              <label htmlFor="church-country">País</label>
              <select id="church-country" name="country" defaultValue="MX">
                {[...COUNTRIES_FIRST, ...COUNTRIES_REST].map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <LocationPicker value={spot} onChange={setSpot} label="Ubicación en el mapa" />
        </fieldset>
        <PlanPicker plans={plans} value={planCode} onChange={setPlanCode} />
        <p className="muted small">
          {manual
            ? 'Tu iglesia se activa en cuanto pagas el plan, o cuando la administración de KEORA la revise y la active.'
            : `Empiezas con ${trialDays} días de prueba sin costo. Al terminar, pagas la mensualidad del plan para seguir modificando datos.`}
        </p>
        <label className="consent">
          <input type="checkbox" name="terms" required />
          <span>
            Acepto, en nombre de la iglesia, los{' '}
            <Link to="/terminos" target="_blank">
              Términos y Condiciones
            </Link>
            , incluido que la mensualidad puede variar de un periodo a otro y que los pagos no son reembolsables.
          </span>
        </label>
        <Button type="submit" busy={busy} className="btn--block">
          Registrar iglesia
        </Button>
      </form>
    </Card>
  );
}

/** Zona horaria del dispositivo de quien registra la Iglesia (se puede cambiar después). */
function browserTimezone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}
